import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useUpdateServiceBook } from '@/hooks/useChecklists';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';

export interface PreInvoiceChecklistDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  workOrderId: string;
  finalInspectionCompleted: boolean;
  laborCount: number;
  partsCount: number;
}

export function PreInvoiceChecklistDialog({
  open,
  onClose,
  onConfirm,
  workOrderId,
  finalInspectionCompleted,
  laborCount,
  partsCount,
}: PreInvoiceChecklistDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tChecklists } = useTranslation('checklists');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateServiceBook = useUpdateServiceBook();
  const [inspectionChecked, setInspectionChecked] = useState(false);
  const [partsConfirmed, setPartsConfirmed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInspectionChecked(false);
    setPartsConfirmed(false);
  }, [open]);

  const inspectionOk = finalInspectionCompleted || inspectionChecked;
  const canContinue = inspectionOk && partsConfirmed && !updateServiceBook.isPending;

  const handleInspectionChange = async (checked: boolean) => {
    if (!checked || finalInspectionCompleted) return;
    setInspectionChecked(true);
    try {
      await updateServiceBook.mutateAsync({
        workOrderId,
        finalInspectionCompleted: true,
      });
    } catch (error) {
      setInspectionChecked(false);
      notify.error(tc('error'), describeApiError(error, tc, { fallback: tChecklists('errors.serviceBookFailed') }));
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;
    onConfirm();
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('preInvoiceChecklist.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('preInvoiceChecklist.title')}</DialogTitle>
      <DialogContent data-testid="pre-invoice-checklist-dialog">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('preInvoiceChecklist.description')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }} data-testid="pre-invoice-line-summary">
          {t('preInvoiceChecklist.lineSummary', { laborCount, partsCount })}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            data-testid="pre-invoice-final-inspection"
            control={
              <Checkbox
                checked={inspectionOk}
                disabled={inspectionOk || updateServiceBook.isPending}
                onChange={(event) => {
                  void handleInspectionChange(event.target.checked);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{tChecklists('finalInspection.completed')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {finalInspectionCompleted
                    ? t('preInvoiceChecklist.finalInspectionAlreadyDone')
                    : t('preInvoiceChecklist.finalInspectionHint')}
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            data-testid="pre-invoice-parts-labor-confirm"
            control={
              <Checkbox
                checked={partsConfirmed}
                onChange={(event) => setPartsConfirmed(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2">{t('preInvoiceChecklist.partsAndLaborConfirm')}</Typography>
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateServiceBook.isPending}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="pre-invoice-continue"
          {...(updateServiceBook.isPending
            ? { startIcon: <CircularProgress size={16} /> }
            : {})}
        >
          {t('preInvoiceChecklist.continue')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
